import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import api from '../services/api';
import UserDetailsModal from '../components/UserDetailsModal';
import BankDetailsEditModal from '../components/BankDetailsEditModal';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

const formatDate = (s) => s ? new Date(s).toLocaleDateString('en-ZA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
const formatMoney = (v) => v == null ? '—' : `R${Number(v).toFixed(2)}`;
const label = (s) => s ? s.replace(/_/g, ' ').toLowerCase() : '—';
const categoryPath = (item) => [item.category, item.subcategory, item.sport].filter(Boolean).join(' › ') || '—';

// The app's main categories (mainCategories in the app's Categories.tsx)
const MAIN_CATEGORIES = [
  'School & sport uniform', 'Club clothing', 'Training wear', 'Belts, bags & shoes', 'Sports equipment',
  'Gym Equipment', 'Textbooks', 'Art & Stationery', 'Matric dance clothing', 'Musical equipment',
  'School props and decor', 'Dancing', 'Educational Toys',
];

const STATUS_COLOURS = {
  AVAILABLE: '#27ae60', SOLD: '#3498db', RESERVED: '#f39c12', EXPIRED: '#95a5a6',
  PENDING_PAYMENT: '#f39c12', PAYMENT_CONFIRMED: '#3498db', PROCESSING: '#3498db',
  SHIPPED: '#8e44ad', DELIVERED: '#27ae60', COMPLETED: '#27ae60', CANCELLED: '#95a5a6',
};

const Badge = ({ value }) => (
  <span style={{
    display: 'inline-block', padding: '3px 8px', borderRadius: '10px', fontSize: '12px',
    color: 'white', backgroundColor: STATUS_COLOURS[value] || '#7f8c8d', textTransform: 'capitalize',
  }}>
    {label(value)}
  </span>
);

const UsersPage = () => {
  const { id } = useParams();
  return id ? <UserActivity userId={id} /> : <UserList />;
};

const UserList = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleFilter, setRoleFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateAdmin, setShowCreateAdmin] = useState(false);
  const [withListings, setWithListings] = useState(false);
  const shown = withListings ? users.filter(u => u.listings_count > 0) : users;

  const fetchUsers = useCallback(async (search) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ role: roleFilter });
      if (search) params.set('search', search);
      const response = await api.get(`/admin/users?${params}`);
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [roleFilter]);

  useEffect(() => { fetchUsers(searchTerm); }, [fetchUsers]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <Toaster position="top-right" />
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '20px' }}>
        <div>
          <h1 style={{ margin: 0 }}>Users</h1>
          <p style={{ color: '#666', margin: '5px 0 0 0' }}>Select a user to see their listings, transaction history and account actions.</p>
        </div>
        <button onClick={() => setShowCreateAdmin(true)} style={{ ...actionBtn, backgroundColor: '#27ae60', padding: '10px 20px', fontSize: '14px', fontWeight: 'bold' }}>
          + Create Admin
        </button>
      </div>

      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          style={{ padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
        >
          <option value="all">All Users</option>
          <option value="seller">Sellers</option>
          <option value="buyer">Buyers</option>
          <option value="both">Buyers &amp; Sellers</option>
          <option value="admin">Admins</option>
        </select>
        <form onSubmit={(e) => { e.preventDefault(); fetchUsers(searchTerm); }} style={{ display: 'flex', gap: '10px', flex: 1 }}>
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
          />
          <button type="submit" style={{ width: 'auto', padding: '8px 16px' }}>Search</button>
        </form>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', cursor: 'pointer' }}>
          <input type="checkbox" checked={withListings} onChange={(e) => setWithListings(e.target.checked)} style={{ width: 'auto' }} />
          Only users with listings
        </label>
      </div>

      {loading ? (
        <p>Loading...</p>
      ) : shown.length === 0 ? (
        <div style={{ backgroundColor: 'white', padding: '40px', borderRadius: '8px', textAlign: 'center', color: '#666' }}>
          No users found.
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', borderRadius: '8px', overflow: 'hidden' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', textAlign: 'left' }}>
                <th style={th}>User</th>
                <th style={th}>Role</th>
                <th style={th}>Status</th>
                <th style={th}>Listings</th>
                <th style={th}>Latest listing</th>
                <th style={th}>Listing value</th>
                <th style={th}>Joined</th>
              </tr>
            </thead>
            <tbody>
              {shown.map(user => (
                <tr
                  key={user.id}
                  onClick={() => navigate(`/users/${user.id}`)}
                  style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f9fc'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
                >
                  <td style={td}>
                    <div style={{ fontWeight: 'bold' }}>
                      {user.first_name} {user.last_name}
                      <ShopTag shop={user.school_shop} />
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>{user.email}</div>
                  </td>
                  <td style={{ ...td, textTransform: 'capitalize' }}>{user.user_type}</td>
                  <td style={td}>
                    <div style={{ textTransform: 'capitalize' }}>{user.status}</div>
                    <div style={{ fontSize: '11px', color: user.email_verified ? '#2e7d32' : '#c0392b' }}>
                      {user.email_verified ? 'email verified' : 'email unverified'}
                    </div>
                  </td>
                  <td style={td}>
                    {user.listings_count > 0 ? (
                      <>
                        <div style={{ fontWeight: 'bold' }}>{user.listings_count}</div>
                        <div style={{ fontSize: '11px', color: '#27ae60' }}>{user.listings_available} available</div>
                      </>
                    ) : <span style={{ color: '#bbb' }}>—</span>}
                  </td>
                  <td style={{ ...td, fontSize: '13px', color: '#666' }}>{formatDate(user.latest_listing_at)}</td>
                  <td style={td}>
                    {user.listings_available > 0 ? (
                      <>
                        <div style={{ fontWeight: 'bold' }}>{formatMoney(user.listings_value)}</div>
                        <div style={{ fontSize: '11px', color: '#666' }}>on sale</div>
                      </>
                    ) : <span style={{ color: '#bbb' }}>—</span>}
                  </td>
                  <td style={{ ...td, fontSize: '13px', color: '#666' }}>{formatDate(user.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreateAdmin && (
        <CreateAdminModal
          onClose={() => setShowCreateAdmin(false)}
          onCreated={() => { setShowCreateAdmin(false); fetchUsers(searchTerm); }}
        />
      )}
    </div>
  );
};

const UserActivity = ({ userId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('listings');
  const [showDetails, setShowDetails] = useState(false);
  const [showBankEdit, setShowBankEdit] = useState(false);
  const [tempPassword, setTempPassword] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async (quiet = false) => {
    try {
      if (!quiet) setLoading(true);
      const response = await api.get(`/admin/users/${userId}/activity`);
      setData(response.data);
    } catch (error) {
      toast.error(error.response?.status === 404 ? 'User not found' : 'Failed to load user activity');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const backButton = (
    <button onClick={() => navigate('/users')} style={{ width: 'auto', padding: '6px 14px', marginBottom: '16px' }}>
      ← All users
    </button>
  );

  if (loading) return <div><Toaster position="top-right" />{backButton}<p>Loading...</p></div>;
  if (!data) return <div><Toaster position="top-right" />{backButton}<p>Could not load this user.</p></div>;

  const { user, summary, listings, purchases, sales } = data;
  const fullName = `${user.first_name} ${user.last_name}`;

  // Runs an account action with a confirm prompt and toasts, then refreshes this user
  const runAction = async ({ confirm, pending, request, success, failure, after }) => {
    if (confirm && !window.confirm(confirm)) return;
    setBusy(true);
    const loadingToast = toast.loading(pending);
    try {
      const res = await request();
      toast.dismiss(loadingToast);
      toast.success(res.data?.message || success, { duration: 5000 });
      if (after) after(res); else load(true);
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.response?.data?.error || failure);
    } finally {
      setBusy(false);
    }
  };

  const changeRole = (newRole) => {
    if (newRole === user.user_type) return;
    runAction({
      confirm: newRole === 'buyer'
        ? 'Change this user to Buyer only? If they have active listings, this will fail.'
        : `Change ${fullName}'s role to ${newRole}?`,
      pending: 'Updating role...',
      request: () => api.put(`/admin/users/${user.id}/role`, { userType: newRole }),
      success: 'User role updated',
      failure: 'Failed to update user role',
    });
  };
  const tabs = [
    { key: 'listings', title: `Listings (${listings.length})` },
    { key: 'purchases', title: `Purchases (${purchases.length})` },
    { key: 'sales', title: `Sales (${sales.length})` },
  ];

  return (
    <div>
      <Toaster position="top-right" />
      {backButton}

      <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', marginBottom: '20px' }}>
        <h1 style={{ margin: '0 0 6px 0' }}>
          {user.first_name} {user.last_name}
          <ShopTag shop={user.school_shop} />
        </h1>
        <div style={{ color: '#666', fontSize: '14px', display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
          <span>{user.email}</span>
          {user.phone && <span>{user.phone}</span>}
          <span style={{ textTransform: 'capitalize' }}>Role: {user.user_type}</span>
          <span style={{ textTransform: 'capitalize' }}>Status: {user.status}</span>
          {(user.user_type === 'seller' || user.user_type === 'both') && (
            <span style={{ textTransform: 'capitalize' }}>Verification: {user.verification_status || '—'}</span>
          )}
          {user.school_name && <span>{user.school_name}</span>}
          <span>Joined {formatDate(user.created_at)}</span>
          <span style={{ color: user.email_verified ? '#2e7d32' : '#c0392b' }}>
            {user.email_verified ? 'Email verified' : 'Email unverified'}
          </span>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #eee' }}>
          <label style={{ fontSize: '13px', color: '#666' }}>Role:</label>
          <select
            value={user.user_type}
            onChange={(e) => changeRole(e.target.value)}
            disabled={busy}
            style={{ padding: '6px 10px', fontSize: '13px', borderRadius: '4px', border: '1px solid #ddd', backgroundColor: 'white', textTransform: 'capitalize', minWidth: '100px' }}
          >
            <option value="buyer">Buyer</option>
            <option value="seller">Seller</option>
            <option value="both">Both</option>
            <option value="admin">Admin</option>
          </select>

          <button onClick={() => setShowDetails(true)} style={{ ...actionBtn, backgroundColor: '#3498db' }}>
            Full details &amp; documents
          </button>
          {['seller', 'both', 'admin'].includes(user.user_type) && (
            <button disabled={busy} onClick={() => setShowBankEdit(true)} style={{ ...actionBtn, backgroundColor: '#16a085' }}>
              Change bank details
            </button>
          )}
          {user.user_type !== 'admin' && (
            <label style={{ ...actionBtn, backgroundColor: '#2980b9', display: 'inline-block', opacity: busy ? 0.6 : 1 }}>
              {user.id_document_url ? 'Replace ID document' : 'Upload ID document'}
              <input
                type="file"
                accept="image/*,.heic,.heif,.pdf,.doc,.docx"
                disabled={busy}
                style={{ display: 'none' }}
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  e.target.value = '';
                  if (!file) return;
                  const formData = new FormData();
                  formData.append('idDocument', file);
                  runAction({
                    confirm: user.id_document_url
                      ? `Replace ${fullName}'s ID document with ${file.name}?`
                      : `Upload ${file.name} as ${fullName}'s ID document?`,
                    pending: 'Uploading ID document...',
                    request: () => api.post(`/admin/users/${user.id}/id-document`, formData, {
                      headers: { 'Content-Type': 'multipart/form-data' }, // the instance default is JSON, which would turn the FormData into JSON
                      timeout: 60000,
                    }),
                    success: 'ID document uploaded',
                    failure: 'Failed to upload ID document',
                  });
                }}
              />
            </label>
          )}
          <button
            disabled={busy}
            onClick={() => runAction({
              confirm: `Reset ${fullName}'s password?`,
              pending: 'Resetting password...',
              request: () => api.put(`/admin/users/${user.id}/reset-password`, {}),
              success: 'Password reset — temporary password emailed to the user.',
              failure: 'Failed to reset password',
              after: (res) => { setTempPassword(res.data?.tempPassword || null); load(true); },
            })}
            style={{ ...actionBtn, backgroundColor: '#f39c12' }}
          >
            Reset password
          </button>
          {!user.email_verified && (
            <button
              disabled={busy}
              onClick={() => runAction({
                pending: 'Sending verification email...',
                request: () => api.post(`/admin/users/${user.id}/resend-verification`, {}),
                success: 'Verification email sent',
                failure: 'Failed to send verification email',
              })}
              style={{ ...actionBtn, backgroundColor: '#8e44ad' }}
            >
              Resend verification
            </button>
          )}
          {user.user_type !== 'admin' && (
            <>
              {user.status === 'active' ? (
                <button
                  disabled={busy}
                  onClick={() => runAction({
                    confirm: `Suspend ${fullName}? They will no longer be able to log in.`,
                    pending: 'Suspending user...',
                    request: () => api.put(`/admin/users/${user.id}/suspend`, {}),
                    success: 'User suspended',
                    failure: 'Failed to suspend user',
                  })}
                  style={{ ...actionBtn, backgroundColor: '#e67e22' }}
                >
                  Suspend
                </button>
              ) : (
                <button
                  disabled={busy}
                  onClick={() => runAction({
                    confirm: `Reactivate ${fullName}?`,
                    pending: 'Reactivating user...',
                    request: () => api.put(`/admin/users/${user.id}/reactivate`, {}),
                    success: 'User reactivated',
                    failure: 'Failed to reactivate user',
                  })}
                  style={{ ...actionBtn, backgroundColor: '#27ae60' }}
                >
                  Reactivate
                </button>
              )}
              <button
                disabled={busy}
                onClick={() => runAction({
                  confirm: `Remove ${fullName}?\n\n`
                    + '• If they have no orders, the account is permanently deleted.\n'
                    + "• If they have order history, it's anonymised (financial records are kept) and hidden from the user list.\n\n"
                    + 'This cannot be undone.',
                  pending: 'Removing user...',
                  request: () => api.delete(`/admin/users/${user.id}`),
                  success: 'User removed',
                  failure: 'Failed to remove user',
                  after: () => navigate('/users'),
                })}
                style={{ ...actionBtn, backgroundColor: '#e74c3c' }}
              >
                Delete
              </button>
            </>
          )}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '12px', marginBottom: '20px' }}>
        <StatCard title="Listings" value={summary.listings_total} sub={`${summary.listings_available} available`} />
        <StatCard title="Purchases" value={summary.purchases_count} sub={`${formatMoney(summary.purchases_value)} paid`} />
        <StatCard title="Sales" value={summary.sales_count} sub={`${formatMoney(summary.sales_value)} sold`} />
        <StatCard title="Paid out to seller" value={formatMoney(summary.payouts_value)} sub="completed payouts" />
      </div>

      <div style={{ display: 'flex', gap: '4px', borderBottom: '2px solid #eee', marginBottom: '0' }}>
        {tabs.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{
              width: 'auto', padding: '10px 18px', border: 'none', borderRadius: '6px 6px 0 0', cursor: 'pointer',
              backgroundColor: tab === t.key ? '#3498db' : '#ecf0f1',
              color: tab === t.key ? 'white' : '#2c3e50', fontWeight: tab === t.key ? 'bold' : 'normal',
            }}
          >
            {t.title}
          </button>
        ))}
      </div>

      {tab === 'listings' && <ListingsTable listings={listings} onMoved={() => load(true)} />}
      {tab === 'purchases' && <OrdersTable orders={purchases} counterparty="seller" empty="This user hasn't bought anything yet." />}
      {tab === 'sales' && <OrdersTable orders={sales} counterparty="buyer" empty="This user hasn't sold anything yet." />}

      {showDetails && <UserDetailsModal user={user} onClose={() => setShowDetails(false)} />}
      {showBankEdit && (
        <BankDetailsEditModal
          user={user}
          current={user}
          onClose={() => setShowBankEdit(false)}
          onSaved={(message) => { setShowBankEdit(false); toast.success(message, { duration: 6000 }); load(true); }}
        />
      )}
      {tempPassword && (
        <TempPasswordModal user={user} password={tempPassword} onClose={() => setTempPassword(null)} />
      )}
    </div>
  );
};

const StatCard = ({ title, value, sub }) => (
  <div style={{ backgroundColor: 'white', padding: '16px', borderRadius: '8px' }}>
    <div style={{ fontSize: '13px', color: '#7f8c8d' }}>{title}</div>
    <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#2c3e50', margin: '4px 0' }}>{value}</div>
    <div style={{ fontSize: '12px', color: '#95a5a6' }}>{sub}</div>
  </div>
);

const EmptyState = ({ text }) => (
  <div style={{ backgroundColor: 'white', padding: '40px', textAlign: 'center', color: '#666', borderRadius: '0 0 8px 8px' }}>{text}</div>
);

const ListingsTable = ({ listings, onMoved }) => {
  const [moving, setMoving] = useState(null);
  if (listings.length === 0) return <EmptyState text="This user has no listings." />;
  return (
    <div style={{ overflowX: 'auto' }}>
      <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#666' }}>Click a listing to move it to another category on the seller's behalf.</p>
      <table style={tableStyle}>
        <thead>
          <tr style={{ backgroundColor: '#f8f9fa', textAlign: 'left' }}>
            <th style={th}>Item</th>
            <th style={th}>Category</th>
            <th style={th}>Price</th>
            <th style={th}>Qty</th>
            <th style={th}>Status</th>
            <th style={th}>Listed</th>
            <th style={th}>Expires</th>
            <th style={th}></th>
          </tr>
        </thead>
        <tbody>
          {listings.map(item => (
            <tr
              key={item.id}
              onClick={() => setMoving(item)}
              style={{ borderBottom: '1px solid #eee', cursor: 'pointer' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#f5f9fc'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
            >
              <td style={td}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  {item.front_photo ? (
                    <img
                      src={item.front_photo.startsWith('http') ? item.front_photo : `${API_URL}${item.front_photo}`}
                      alt=""
                      style={{ width: '44px', height: '44px', objectFit: 'cover', borderRadius: '4px' }}
                    />
                  ) : (
                    <div style={{ width: '44px', height: '44px', backgroundColor: '#ecf0f1', borderRadius: '4px' }} />
                  )}
                  <div>
                    <div style={{ fontWeight: 'bold' }}>{item.item_name}</div>
                    <div style={{ fontSize: '12px', color: '#888' }}>
                      {[item.school_name, item.size && `Size ${item.size}`].filter(Boolean).join(' · ')}
                    </div>
                  </div>
                </div>
              </td>
              <td style={td}>{categoryPath(item)}</td>
              <td style={td}>{formatMoney(item.price)}</td>
              <td style={td}>{item.quantity}</td>
              <td style={td}><Badge value={item.status} /></td>
              <td style={{ ...td, fontSize: '13px', color: '#666' }}>{formatDate(item.created_at)}</td>
              <td style={{ ...td, fontSize: '13px', color: '#666' }}>{formatDate(item.expiry_date)}</td>
              <td style={td}>
                <button type="button" style={{ ...actionBtn, backgroundColor: '#3498db' }}>Move</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {moving && (
        <MoveListingModal
          item={moving}
          onClose={() => setMoving(null)}
          onMoved={() => { setMoving(null); onMoved(); }}
        />
      )}
    </div>
  );
};

/**
 * Moves a listing to another category for the seller. Sub-category and sport suggest the
 * spots other listings in that category already use; the seller gets a notification.
 */
const MoveListingModal = ({ item, onClose, onMoved }) => {
  const [paths, setPaths] = useState([]);
  const [category, setCategory] = useState(item.category || '');
  const [subcategory, setSubcategory] = useState(item.subcategory || '');
  const [sport, setSport] = useState(item.sport || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/items/category-paths').then(r => setPaths(r.data)).catch(() => setPaths([]));
  }, []);

  const categories = MAIN_CATEGORIES.includes(item.category) || !item.category ? MAIN_CATEGORIES : [item.category, ...MAIN_CATEGORIES];
  const inCategory = paths.filter(p => p.category === category);
  const unique = (values) => [...new Set(values.filter(Boolean))].sort();
  const subcategories = unique(inCategory.map(p => p.subcategory));
  const sports = unique(inCategory.filter(p => !subcategory.trim() || p.subcategory === subcategory.trim()).map(p => p.sport));
  const sameSpot = inCategory.find(p => (p.subcategory || '') === subcategory.trim() && (p.sport || '') === sport.trim());
  const unchanged = category === (item.category || '') && subcategory.trim() === (item.subcategory || '') && sport.trim() === (item.sport || '');
  const target = [category, subcategory.trim(), sport.trim()].filter(Boolean).join(' › ');

  const save = async (e) => {
    e.preventDefault();
    if (!window.confirm(`Move "${item.item_name}" to ${target}?\n\nThe seller gets a notification that it moved.`)) return;
    setSaving(true);
    try {
      const res = await api.put(`/admin/items/${item.id}/category`, { category, subcategory: subcategory.trim(), sport: sport.trim() });
      toast.success(res.data?.message || 'Listing moved', { duration: 5000 });
      onMoved();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Could not move the listing');
      setSaving(false);
    }
  };

  const fieldLabel = { display: 'block', margin: '14px 0 5px', fontWeight: 'bold' };
  const input = { width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd', boxSizing: 'border-box' };

  return (
    <div onClick={(e) => e.stopPropagation()} style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, cursor: 'default' }}>
      <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', maxWidth: '500px', width: '90%' }}>
        <h2 style={{ marginTop: 0, marginBottom: '4px' }}>Move listing</h2>
        <p style={{ margin: 0, color: '#666' }}><strong>{item.item_name}</strong> is in {categoryPath(item)}</p>
        <form onSubmit={save}>
          <label style={fieldLabel}>Category *</label>
          <select value={category} onChange={(e) => { setCategory(e.target.value); setSubcategory(''); setSport(''); }} required style={input}>
            <option value="" disabled>Choose a category</option>
            {categories.map(c => <option key={c} value={c}>{c}</option>)}
          </select>

          <label style={fieldLabel}>Sub-category</label>
          <input list="move-subcategories" value={subcategory} onChange={(e) => setSubcategory(e.target.value)}
            placeholder={subcategories.length ? 'Pick or type one' : 'Optional'} style={input} />
          <datalist id="move-subcategories">{subcategories.map(v => <option key={v} value={v} />)}</datalist>

          <label style={fieldLabel}>Sport</label>
          <input list="move-sports" value={sport} onChange={(e) => setSport(e.target.value)}
            placeholder={sports.length ? 'Pick or type one' : 'Optional — for sports items'} style={input} />
          <datalist id="move-sports">{sports.map(v => <option key={v} value={v} />)}</datalist>

          {category && !unchanged && (
            <p style={{ fontSize: '13px', margin: '12px 0 0', color: sameSpot ? '#27ae60' : '#e67e22' }}>
              {sameSpot
                ? `${sameSpot.listings} other listing${sameSpot.listings === 1 ? ' is' : 's are'} already in ${target}.`
                : `No listings are in ${target} yet — check the spelling matches the app, or buyers browsing the app may not find it.`}
            </p>
          )}

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button type="button" onClick={onClose} style={{ width: 'auto', padding: '10px 20px', borderRadius: '4px', border: '1px solid #ddd', backgroundColor: 'white', color: '#2c3e50', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="submit" disabled={saving || !category || unchanged}
              style={{ ...actionBtn, backgroundColor: saving || !category || unchanged ? '#bdc3c7' : '#3498db', padding: '10px 20px', fontSize: '14px', fontWeight: 'bold' }}>
              {saving ? 'Moving…' : 'Move listing'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const OrdersTable = ({ orders, counterparty, empty }) => {
  if (orders.length === 0) return <EmptyState text={empty} />;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={tableStyle}>
        <thead>
          <tr style={{ backgroundColor: '#f8f9fa', textAlign: 'left' }}>
            <th style={th}>Order</th>
            <th style={th}>Item</th>
            <th style={th}>{counterparty === 'seller' ? 'Seller' : 'Buyer'}</th>
            <th style={th}>Amount</th>
            <th style={th}>Order status</th>
            <th style={th}>Payment / Escrow</th>
            <th style={th}>Payout</th>
          </tr>
        </thead>
        <tbody>
          {orders.map(o => (
            <tr key={o.order_number} style={{ borderBottom: '1px solid #eee' }}>
              <td style={td}>
                <div style={{ fontWeight: 'bold' }}>{o.order_number}</div>
                <div style={{ fontSize: '12px', color: '#888' }}>{formatDate(o.created_at)}</div>
                {o.tradesafe_transaction_id && (
                  <div style={{ fontSize: '11px', color: '#aaa' }}>TradeSafe {o.tradesafe_transaction_id}</div>
                )}
              </td>
              <td style={td}>
                {o.item_name || '—'}
                {o.quantity > 1 && <span style={{ color: '#888' }}> × {o.quantity}</span>}
              </td>
              <td style={td}>
                {o[`${counterparty}_name`]}
                <div style={{ fontSize: '12px', color: '#888' }}>{o[`${counterparty}_email`]}</div>
              </td>
              <td style={td}>
                <div>{formatMoney(o.total_amount)}</div>
                <div style={{ fontSize: '12px', color: '#888' }}>
                  item {formatMoney(o.item_price)} + shipping {formatMoney(o.shipping_cost)}
                </div>
              </td>
              <td style={td}>
                <Badge value={o.order_status} />
                {o.dispute_status && o.dispute_status !== 'NONE' && (
                  <div style={{ fontSize: '12px', color: '#c0392b', marginTop: '4px', textTransform: 'capitalize' }}>
                    Dispute: {label(o.dispute_status)}
                  </div>
                )}
                {o.tracking_number && <div style={{ fontSize: '12px', color: '#888', marginTop: '4px' }}>Tracking {o.tracking_number}</div>}
              </td>
              <td style={{ ...td, fontSize: '13px', textTransform: 'capitalize' }}>
                <div>{label(o.payment_status)}</div>
                <div style={{ color: '#888' }}>{label(o.escrow_status)}</div>
              </td>
              <td style={{ ...td, fontSize: '13px' }}>
                <div>{formatMoney(o.seller_payout)}</div>
                <div style={{ color: '#888', textTransform: 'capitalize' }}>{label(o.payout_status)}</div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const EMPTY_ADMIN = { email: '', password: '', firstName: '', lastName: '', phone: '' };

const copyText = async (text, what) => {
  try {
    await navigator.clipboard.writeText(text);
    toast.success(`${what} copied`);
  } catch (e) {
    toast.error('Could not copy — select the text and copy it manually');
  }
};

// Shown after a password reset so the admin can also send the temporary password via WhatsApp
const TempPasswordModal = ({ user, password, onClose }) => {
  const message = `Hi ${user.first_name}, your Eduthrift password has been reset. Your temporary password is: ${password}\n`
    + 'Please log in with it and you will be asked to choose a new password.';

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', maxWidth: '500px', width: '90%' }}>
        <h2 style={{ marginTop: 0 }}>Temporary password</h2>
        <p style={{ color: '#666', marginTop: 0 }}>
          Emailed to {user.email}. You can also copy it and send it to {user.first_name} via WhatsApp{user.phone ? ` (${user.phone})` : ''}.
        </p>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '20px' }}>
          <code style={{ flex: 1, padding: '10px 12px', fontSize: '18px', backgroundColor: '#f4f6f8', border: '1px solid #ddd', borderRadius: '4px', userSelect: 'all', wordBreak: 'break-all' }}>
            {password}
          </code>
          <button onClick={() => copyText(password, 'Password')} style={{ ...actionBtn, backgroundColor: '#3498db', padding: '10px 14px' }}>
            Copy
          </button>
        </div>
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          <button onClick={() => copyText(message, 'WhatsApp message')} style={{ ...actionBtn, backgroundColor: '#25d366', padding: '10px 20px', fontSize: '14px' }}>
            Copy WhatsApp message
          </button>
          <button onClick={onClose} style={{ width: 'auto', padding: '10px 20px', borderRadius: '4px', border: '1px solid #ddd', backgroundColor: 'white', color: '#2c3e50', cursor: 'pointer' }}>
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

const CreateAdminModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState(EMPTY_ADMIN);

  const submit = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading('Creating admin user...');
    try {
      await api.post('/auth/register', { ...form, userType: 'admin', suburb: 'N/A', town: 'N/A', province: 'N/A' });
      toast.dismiss(loadingToast);
      toast.success('Admin user created successfully');
      onCreated();
    } catch (error) {
      toast.dismiss(loadingToast);
      toast.error(error.response?.data?.error || 'Failed to create admin user');
    }
  };

  const field = (key, title, props = {}) => (
    <div style={{ marginBottom: '15px' }}>
      <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>{title}</label>
      <input
        value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
        {...props}
      />
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ backgroundColor: 'white', padding: '30px', borderRadius: '8px', maxWidth: '500px', width: '90%' }}>
        <h2 style={{ marginTop: 0 }}>Create New Admin User</h2>
        <form onSubmit={submit}>
          {field('email', 'Email *', { type: 'email', required: true })}
          {field('password', 'Password *', { type: 'password', required: true, minLength: 8 })}
          {field('firstName', 'First Name *', { type: 'text', required: true })}
          {field('lastName', 'Last Name', { type: 'text' })}
          {field('phone', 'Phone *', { type: 'tel', required: true })}
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
            <button type="button" onClick={onClose} style={{ width: 'auto', padding: '10px 20px', borderRadius: '4px', border: '1px solid #ddd', backgroundColor: 'white', color: '#2c3e50', cursor: 'pointer' }}>
              Cancel
            </button>
            <button type="submit" style={{ ...actionBtn, backgroundColor: '#27ae60', padding: '10px 20px', fontSize: '14px', fontWeight: 'bold' }}>
              Create Admin
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const actionBtn = { width: 'auto', padding: '6px 12px', fontSize: '13px', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' };
const tableStyle = { width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' };
const th = { padding: '12px 15px', borderBottom: '2px solid #eee', fontSize: '14px' };
const td = { padding: '12px 15px', verticalAlign: 'top' };

/** Marks a school second-hand shop's owner or staff member, next to their name. */
function ShopTag({ shop }) {
  if (!shop) return null;
  const pending = shop.status !== 'verified';
  return (
    <span title={`${shop.role === 'owner' ? 'Owner' : 'Staff'} of ${shop.name}`} style={{
      display: 'inline-block', marginLeft: '8px', padding: '2px 8px', borderRadius: '10px',
      backgroundColor: '#F6EAFB', border: '1px solid #E3C2EE', color: '#6B1585',
      fontSize: '12px', fontWeight: 600, verticalAlign: 'middle', whiteSpace: 'nowrap'
    }}>
      🏫 {shop.name} · {shop.role === 'owner' ? 'Owner' : 'Staff'}
      {pending ? ` (${shop.status === 'rejected' ? 'rejected' : 'under review'})` : ''}
    </span>
  );
}

export default UsersPage;
